import React, { useContext } from 'react';
import {
  CBadge,
  CHeader,
  CHeaderBrand,
  CHeaderNav,
  CImg,
  CNav,
  CNavItem,
  CNavLink,
  CSidebarBrand,
} from '@coreui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as pluralize from 'pluralize';
import { useSelector } from 'react-redux';
import { selectCanAccessCustomization, selectJourneyConfigChatEnabled } from '../store/selectors/journey.selectors';
import { selectUnreadChats } from '../chat/selectors/chat.selector';
import ProfileDropdown from './ProfileDropdown';
import { SideBarState } from './GeneralLayoutSideMenu';
import logo from '../assets/images/glympse-pro-white.png';
import question_mark from '../assets/images/question_mark.svg';
import logoMinimized from '../assets/images/Glympse-Pro-Logo-Transparent-Adaptive-Inner.png';
import { ConfigContext } from '../shared/contexts/journeyConfig.context';
import { isInactiveSubscription } from '../shared/utils/chargebee-utils';
import { selectUserOrganization } from '../store/selectors/store.selectors';
import OrgHierarchyButton from '../shared/components/orgHierarchy/OrgHierarchyButton';

export interface GeneralLayoutHeaderProps {
  showMenu: SideBarState;
  onMenuToggle: (val: SideBarState) => void;
}

const GeneralLayoutHeader = (props: GeneralLayoutHeaderProps) => {
  const { showMenu, onMenuToggle } = props;
  const labels = useContext(ConfigContext);
  const org = useSelector(selectUserOrganization);
  const chatEnabled = useSelector(selectJourneyConfigChatEnabled);
  const canAccessCustomization = useSelector(selectCanAccessCustomization);
  const unreadChats = useSelector(selectUnreadChats);
  const {
    enableCustomizationModule,
    enableInsightsModule,
    enableJobsModule,
    enableDashboardModule,
    enableTechniciansModule,
    enableChargebeeModule,
    enableLiveMapModule,
    enableChatModule,
    enableInternalSignUp
  } = useFlags();

  const toggleSidebarMobile = () => {
    const val = [false, 'responsive'].includes(showMenu) ? true : 'responsive'
    onMenuToggle(val);
  }

  const toggleSideBarOnKeyDown = (e) => {
    if ((e.key === 'Enter' || e.keyCode === 13) && (e?.target?.classList || []).contains("glympse-mobileMenu")) {
      toggleSidebarMobile();
    }
  }

  return (
    <>
      <CHeader withSubheader className="glympser-mainNav">
        <div className="glympser-navLeft">
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <div
            role="button"
            tabIndex={0}
            className="d-lg-none glympse-mobileMenu"
            onClick={toggleSidebarMobile}
            onKeyDown={toggleSideBarOnKeyDown}
          >
            <div className="glympse-mobileMenuLine"/>
            <div className="glympse-mobileMenuLine"/>
            <div className="glympse-mobileMenuLine"/>
          </div>
          <CHeaderBrand className="mx-auto d-lg-none" to="/">
            <CImg src={logo} width="178" alt="Logo"/>
          </CHeaderBrand>

          <CSidebarBrand className="d-md-down-none" to="/">
            <CImg
              className="c-sidebar-brand-full"
              src={logo}
              height="25"
            />
            <CImg
              className="c-sidebar-brand-minimized"
              src={logoMinimized}
              height="60"
            />
          </CSidebarBrand>

          <div className="glympse-desktopNav">
             <OrgHierarchyButton />
             <CNav>
                <CNavItem>
                  {
                    (!enableChargebeeModule || !isInactiveSubscription(org)) && enableDashboardModule && <CNavLink
                      name="Dashboard"
                      exact
                      to="/dashboard">
                      Dashboard
                    </CNavLink>
                  }
                  {
                    (!enableChargebeeModule || !isInactiveSubscription(org)) && enableLiveMapModule && <CNavLink
                      name="Live Map"
                      to="/dashboard/livemap">
                      Live Map
                    </CNavLink>
                  }
                  {
                    (!enableChargebeeModule || !isInactiveSubscription(org)) && enableJobsModule && <CNavLink
                      id="jobs-link"
                      name={`${pluralize(labels.task)}`}
                      to="/jobs/search">
                      {`${pluralize(labels.task)}`}
                    </CNavLink>
                  }
                  {
                    (!enableChargebeeModule || !isInactiveSubscription(org)) && enableTechniciansModule && <CNavLink
                      name={`${pluralize(labels.agent)}`}
                      to="/technicians">
                      {`${pluralize(labels.agent)}`}
                    </CNavLink>
                  }
                  {
                    (!enableChargebeeModule || !isInactiveSubscription(org)) && enableInsightsModule && <CNavLink
                      name="Insights"
                      to="/insights">
                      Insights
                    </CNavLink>
                  }
                  {
                    (!enableChargebeeModule || !isInactiveSubscription(org)) && enableCustomizationModule && canAccessCustomization && <CNavLink
                      name="Customize"
                      to="/customize">
                      Customize
                    </CNavLink>
                  }
                  {
                    enableChatModule && chatEnabled && <CNavLink
                      name="Chats"
                      to="/chat">
                      Chats
                      {unreadChats > 0 && <CBadge color="danger" className="ml-1 chat-nav-badge">{unreadChats}</CBadge>}
                    </CNavLink>
                  }
                  {
                    enableInternalSignUp && <CNavLink
                      name="Internal Sign Up"
                      to="/internal-sign-up">
                      Internal Sign Up
                    </CNavLink>
                  }
                </CNavItem>
              </CNav>
          </div>
        </div>

        <CHeaderNav className="glympser-profileNavWrapper">
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <a href="https://support.glympse.com/topic/glympse-pro/" target="_blank" rel="noreferrer">
            <CImg src={question_mark} className="glympser-questionMark"/>
          </a>
          <ProfileDropdown/>
        </CHeaderNav>
      </CHeader>
      <div className="glympse-desktopNavShadow"/>
    </>
  )
}

export default GeneralLayoutHeader;
